import type { CharitySearchItem, CharitySearchResponse, CharitySuggestPayload, CharitySuggestResponse } from '../../types/pifster-contracts';

/**
 * Search charities by query string.
 *
 * @throws {Error} On non-OK HTTP responses (fail-fast).
 */
export async function searchCharities(
  query: string,
  endpoint: string,
  signal?: AbortSignal,
): Promise<CharitySearchItem[]> {
  const url = `${endpoint}?q=${encodeURIComponent(query)}&limit=8`;

  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Accept': 'application/json' },
    signal: signal ?? null,
  });

  if (!response.ok) {
    throw new Error(`Search failed: HTTP ${String(response.status)}`);
  }

  const body = (await response.json()) as CharitySearchResponse;
  return body.items ?? [];
}

/**
 * Submit a charity suggestion.
 *
 * @throws {Error} On non-OK HTTP responses with the server message when available.
 */
export async function submitSuggestion(
  payload: CharitySuggestPayload,
  endpoint: string,
  nonce: string,
): Promise<CharitySuggestResponse> {
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-WP-Nonce': nonce,
    },
    body: JSON.stringify(payload),
  });

  let body: CharitySuggestResponse = {};
  try {
    body = (await response.json()) as CharitySuggestResponse;
  } catch {
    // Response may not be JSON on certain server errors.
  }

  if (!response.ok) {
    throw new Error(body.message ?? 'Submission failed.');
  }

  return body;
}
